import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth";

const initialState = {
    user: {
        email: '',
        groups: [],
    },
    status: 'idle',
    error: null,
}

export const fetchAuthenticatedUser = createAsyncThunk(
    'authentication/fetchAuthenticatedUser',
    async (_, { rejectWithValue }) => {
        const maxAttempts = 3
        const retryDelayMs = 1000
        const sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                await fetchAuthSession({ forceRefresh: attempt > 1 })
                const response = await fetchUserAttributes()

                if (response?.['custom:company']) {
                    return response
                }

                if (attempt < maxAttempts) {
                    await sleep(retryDelayMs)
                }
            } catch (error) {
                if (attempt === maxAttempts) {
                    //console.log('userAttributes error: ', error)
                    return rejectWithValue({
                        type: 'auth_fetch_failed',
                        message: error?.message || 'Unable to fetch authenticated user',
                    })
                }

                await sleep(retryDelayMs)
            }
        }

        return rejectWithValue({
            type: 'missing_company',
            message: 'custom:company missing after token refresh attempts',
        })
    }
)

export const authenticationSlice = createSlice({
    name: 'authentication',
    initialState,
    extraReducers(builder) {
        builder
            // Redux Toolkit allows us to write "mutating" logic in reducers. It
            // doesn't actually mutate the state because it uses the immer library,
            // which detects changes to a "draft state" and produces a brand new
            // immutable state based off those changes
            .addCase(fetchAuthenticatedUser.pending, (state) => {
                state.status = 'loading'
                state.error = null
            })
            .addCase(fetchAuthenticatedUser.fulfilled, (state, action) => {
                state.user = action.payload
                state.status = 'succeeded'
                state.error = null
            })
            .addCase(fetchAuthenticatedUser.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.payload || { type: 'auth_fetch_failed', message: action.error?.message || 'Unable to fetch authenticated user' }
            })
    },
})

export const { setAuthenticatedUser } = authenticationSlice.actions

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.session.value)`
export const getAuthenticatedUser = (state) => { return state.value }

export default authenticationSlice.reducer
